import { useRef } from 'react';
import type { ChangeEvent } from 'react';
import { SUPPORTED_FORMATS } from './studioData';

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

type Props = {
  fileName: string;
  onFile: (name: string, ext: string) => void;
  pastedText: string;
  onPastedTextChange: (value: string) => void;
  onUsePastedText: () => void;
};

export default function UploadPanel({ fileName, onFile, pastedText, onPastedTextChange, onUsePastedText }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase() : '';
    onFile(file.name, ext);
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center text-lg"><i className="ri-upload-2-line"></i></span>
        <h3 className="font-heading font-bold text-xl text-foreground-950">Upload story</h3>
      </div>
      <p className="mt-3 text-sm text-foreground-600">
        Start from a document, or paste your text to populate the editor. Uploaded documents are not processed in this prototype.
      </p>

      <div className="mt-4">
        <span className={labelClass}>Choose a file</span>
        <input
          ref={fileRef}
          type="file"
          accept=".docx,.pdf,.txt"
          onChange={handleFile}
          className="w-full text-sm text-foreground-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-foreground-950 file:text-background-50 file:font-label file:uppercase file:tracking-wider file:text-xs file:cursor-pointer cursor-pointer"
        />
        {fileName && (
          <p className="mt-2 text-xs text-foreground-600">
            Selected: <span className="font-medium text-foreground-900">{fileName}</span>
          </p>
        )}
      </div>

      <div className="mt-4 rounded-md border border-background-200 bg-background-100 p-4">
        <p className="font-label uppercase tracking-widest text-[11px] text-foreground-600">Supported formats</p>
        <ul className="mt-3 space-y-2.5">
          {SUPPORTED_FORMATS.map((f) => (
            <li key={f.ext} className="flex items-start gap-3">
              <span className={`w-5 h-5 flex items-center justify-center mt-0.5 shrink-0 ${f.supportedInPrototype ? 'text-primary-600' : 'text-foreground-400'}`}>
                <i className={f.supportedInPrototype ? 'ri-checkbox-circle-fill' : 'ri-time-line'}></i>
              </span>
              <span className="text-sm leading-snug">
                <span className="text-foreground-900 font-medium">{f.file}</span>
                <span className="text-foreground-500 ml-2 font-label uppercase tracking-wide text-[10px]">{f.ext}</span>
                <span className="block text-xs text-foreground-500 mt-0.5">{f.note}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-4">
        <label htmlFor="ss-paste" className={labelClass}>Paste article text</label>
        <textarea
          id="ss-paste"
          rows={6}
          value={pastedText}
          onChange={(e) => onPastedTextChange(e.target.value)}
          placeholder="Paste the article text here to populate the editor in this demo."
          className={`${fieldClass} resize-y`}
        ></textarea>
        <button
          type="button"
          onClick={onUsePastedText}
          disabled={!pastedText.trim()}
          className="mt-3 w-full bg-foreground-950 hover:bg-foreground-800 disabled:opacity-40 disabled:cursor-not-allowed text-background-50 font-label font-bold uppercase tracking-wider py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
        >
          Use pasted text in editor
        </button>
      </div>

      <p className="mt-4 bg-secondary-50 border border-secondary-200 text-secondary-900 text-xs rounded-md px-3.5 py-3 flex items-start gap-2">
        <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-information-line"></i></span>
        <span><strong className="font-semibold">Document processing available in production version.</strong> File contents are not extracted or parsed by this prototype — only pasted text reaches the editor.</span>
      </p>
    </div>
  );
}